import React, { useMemo } from 'react';
import { Select, createListCollection } from '@ark-ui/react/select';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

const POPOVER_POSITIONING = {
  sameWidth: true,
  placement: 'bottom',
  gutter: 4,
} as const;

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectInputProps extends BaseInputProps<string> {
  options: SelectOption[];
  /**
   * Když je `true`, select je zakázaný a propisuje stav načítání.
   * Pozn.: `<FormField isLoading>` dělá totéž centrálně — tento prop slouží
   * pro použití mimo FormField.
   */
  isLoading?: boolean;
  /** Text prázdné (placeholder) volby; fallback je `placeholder` z FormField. */
  placeholderOption?: string;
  className?: string;
}

/**
 * Atomický select postavený na Ark UI `Select`.
 *
 * RHF pracuje s jednou `string` hodnotou; Ark interně používá pole hodnot, tudíž
 * mapujeme `value` ↔ `[value]`. QA atributy se propisují na `Select.Trigger`.
 * `ids.label` míří na popisek z `<FormField />` (zag z něj odvodí
 * `aria-labelledby` triggeru).
 */
export const SelectInput: React.FC<SelectInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  options,
  isLoading,
  placeholderOption,
  className,
}) => {
  const collection = useMemo(
    () => createListCollection({ items: options }),
    [options],
  );

  const controlledValue = value ? [value] : [];

  const ids = useMemo(
    () => ({ trigger: id, label: labelId }),
    [id, labelId],
  );

  return (
    <Select.Root
      ids={ids}
      collection={collection}
      value={controlledValue}
      disabled={disabled || isLoading}
      readOnly={readOnly}
      invalid={hasError}
      onValueChange={(details) => {
        onChange(details.value[0] ?? '');
        // Výběr hodnoty = dokončená interakce → touched (validace v módu
        // `onTouched` se má spustit hned, ne až po odchodu z pole).
        onBlur();
      }}
      onInteractOutside={onBlur}
      positioning={POPOVER_POSITIONING}
      lazyMount
      unmountOnExit
      className={clsx(className)}
    >
      <Select.Control>
        <Select.Trigger
          ref={ref}
          onBlur={(event: React.FocusEvent<HTMLButtonElement>) => {
            // Blur triggeru hlásíme jen u zavřeného selectu — při otevření
            // fokus přechází do obsahu a pole by se označilo jako touched
            // uprostřed interakce.
            if (event.currentTarget.dataset['state'] !== 'open') onBlur();
          }}
          className={styles.selectTrigger}
          aria-describedby={describedBy}
          aria-busy={isLoading || undefined}
          {...testAttributes}
        >
          <Select.ValueText placeholder={placeholderOption ?? placeholder} />
          <Select.Indicator className={styles.selectIndicator}>
            <Icon name="chevron-down" size={20} />
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Select.HiddenSelect name={name} />
      <Portal>
        <Select.Positioner>
          <Select.Content
            className={styles.content}
            data-testid={`${testAttributes['data-testid']}-content`}
          >
            {options.map((option) => (
              <Select.Item
                key={option.value}
                item={option}
                className={styles.item}
                data-testid={`${testAttributes['data-testid']}-option-${option.value}`}
              >
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator className={styles.itemIndicator}>
                  <Icon name="check" size={16} />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
};

SelectInput.displayName = 'SelectInput';
